import { Badge, Card, formatDateTime } from '@catering/ui';
import { History } from 'lucide-react';
import type { JSX } from 'react';

import type { ProductionAddendumSummary } from '../../../production/api';

export interface AddendaBlockProps {
  addenda: ProductionAddendumSummary[];
}

/**
 * `production-addenda/spec.md` → "Bloque de addendums oculto/visible":
 * lists every registered post-close change (added/removed/modified,
 * old → new) as a section separate from the original consolidation.
 * Shared by `ProductionSheetViewPage` and `ProductionSheetPrintPage`.
 */
export function AddendaBlock({ addenda }: AddendaBlockProps): JSX.Element | null {
  if (addenda.length === 0) {
    return null;
  }

  return (
    <Card data-testid="addenda-block">
      <div className="mb-4 flex items-center gap-2">
        <History aria-hidden="true" className="size-5 text-slate-500" />
        <h2 className="text-base font-semibold text-slate-900">Cambios posteriores al cierre</h2>
        <Badge>{addenda.length}</Badge>
      </div>

      <ul className="flex flex-col gap-4">
        {addenda.map((addendum, index) => (
          // No stable id in this DTO (`ProductionAddendumSummary`) — `appliedAt` is
          // unique per real registration, `index` only breaks a tie in a test fixture.
          <li key={`${addendum.appliedAt}-${index}`} className="border-l-2 border-slate-200 pl-4">
            <p className="text-xs text-slate-500">
              {formatDateTime(addendum.appliedAt)}
              {addendum.notes === null || addendum.notes === '' ? null : ` · ${addendum.notes}`}
            </p>
            <ul className="mt-1 flex flex-col gap-1 text-sm">
              {addendum.changes.added.map((line) => (
                <li key={`added-${line.menuId}`} className="text-emerald-700">
                  + {line.menuName}: {line.totalQuantity}
                </li>
              ))}
              {addendum.changes.removed.map((line) => (
                <li key={`removed-${line.menuId}`} className="text-red-700">
                  − {line.menuName}
                </li>
              ))}
              {addendum.changes.modified.map((line) => (
                <li key={`modified-${line.menuId}`} className="text-slate-800">
                  {line.menuName}: {line.previousQuantity} → {line.totalQuantity}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Card>
  );
}
